import { useEffect, useState } from 'react';
import { ExtensionDefinition, getFileViewerLinkByDefinition, MediaType } from './model';
import { useViewerChoice } from './useViewerChoice';
import { getViewerOptions, sanitizeViewer } from './viewerChoice';
import { detectSpineJson } from './viewers/common/spine/detectSpineJson';
type Detection = { src: string; status: 'loading' | 'ready' | 'error'; viewer?: MediaType };
export function useAutomaticViewerChoice(
  definition: ExtensionDefinition | null | undefined,
  originalSrc: string | null | undefined,
  { allowManual = true }: { allowManual?: boolean } = {},
) {
  const [manual, setViewer] = useViewerChoice(definition, originalSrc);
  const [detection, setDetection] = useState<Detection | null>(null);
  const [attempt, setAttempt] = useState(0);
  const retry = () => {
    setDetection(null);
    setAttempt((value) => value + 1);
  };
  const detect =
    definition?.extension === 'json' && !!definition.alternativeViewers?.includes(MediaType.Spine);
  // Identity retains the canonical URL; requests follow the viewer's local proxy rewrite.
  const fetchSrc =
    detect && definition && originalSrc
      ? getFileViewerLinkByDefinition(definition, originalSrc, '')
      : '';
  useEffect(() => {
    if (!detect) return;
    const controller = new AbortController();
    const src = originalSrc ?? '';
    setDetection({ src, status: 'loading' });
    void detectSpineJson({ src: fetchSrc, signal: controller.signal }).then(
      (spine) => {
        if (!controller.signal.aborted)
          setDetection({ src, status: 'ready', viewer: spine ? MediaType.Spine : MediaType.Text });
      },
      () => {
        if (!controller.signal.aborted) setDetection({ src, status: 'error' });
      },
    );
    return () => controller.abort();
  }, [detect, originalSrc, fetchSrc, attempt]);
  const override =
    allowManual && definition && manual !== null && getViewerOptions(definition).includes(manual)
      ? manual
      : null;
  if (override !== null) return { viewer: override, status: 'ready' as const, setViewer, retry };
  if (!detect)
    return {
      viewer: definition ? sanitizeViewer(definition, null) : null,
      status: 'ready' as const,
      setViewer,
      retry,
    };
  const current = detection?.src === (originalSrc ?? '') ? detection : null;
  return {
    viewer: current?.viewer ?? null,
    status: current?.status ?? 'loading',
    errorCode: current?.status === 'error' ? ('source-load-error' as const) : undefined,
    setViewer,
    retry,
  };
}
